import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  // ChartBarIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  EyeIcon,
  BookOpenIcon,
  HeartIcon,
  ShareIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { diaryService } from '../../services/diary';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';

const AnalyticsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30'); // days

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      const response = await diaryService.getStats();
      setStats(response);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setLoading(false);
    }
  };

  // Mock data for charts - replace with real data from backend
  const mockWritingTrend = [
    { date: '2024-01-01', entries: 2 },
    { date: '2024-01-02', entries: 1 },
    { date: '2024-01-03', entries: 3 },
    { date: '2024-01-04', entries: 0 },
    { date: '2024-01-05', entries: 2 },
    { date: '2024-01-06', entries: 1 },
    { date: '2024-01-07', entries: 4 }
  ];

  const mockMoodData = [
    { name: 'Happy', value: 35, color: '#10B981' },
    { name: 'Peaceful', value: 25, color: '#3B82F6' },
    { name: 'Excited', value: 20, color: '#F59E0B' },
    { name: 'Thoughtful', value: 15, color: '#8B5CF6' },
    { name: 'Other', value: 5, color: '#6B7280' }
  ];

  const mockTagData = [
    { tag: 'personal', count: 45 },
    { tag: 'work', count: 32 },
    { tag: 'travel', count: 28 },
    { tag: 'family', count: 25 },
    { tag: 'goals', count: 18 }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-paper-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-serif font-bold mb-2">Analytics Dashboard</h1>
            <p className="text-purple-100 text-lg">
              Insights into your journaling journey
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Time Range Filter */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Time Range:</span>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 3 months</option>
              <option value="365">Last year</option>
            </select>
          </div>
        </motion.div>

        {/* Overview Stats */}
        {stats && (
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="card p-6 text-center">
              <BookOpenIcon className="h-8 w-8 text-purple-600 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-ink-black">
                {stats.overview.totalEntries}
              </h3>
              <p className="text-gray-600">Total Entries</p>
            </div>
            <div className="card p-6 text-center">
              <HeartIcon className="h-8 w-8 text-red-500 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-ink-black">
                {stats.overview.pinnedEntries}
              </h3>
              <p className="text-gray-600">Pinned Entries</p>
            </div>
            <div className="card p-6 text-center">
              <ShareIcon className="h-8 w-8 text-blue-500 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-ink-black">
                {stats.overview.sharedEntries}
              </h3>
              <p className="text-gray-600">Shared Entries</p>
            </div>
            <div className="card p-6 text-center">
              <EyeIcon className="h-8 w-8 text-green-500 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-ink-black">
                {stats.overview.totalViews}
              </h3>
              <p className="text-gray-600">Total Views</p>
            </div>
          </motion.div>
        )}

        {/* Charts Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Writing Trend Chart */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h3 className="text-lg font-semibold text-ink-black mb-4 flex items-center">
              <ArrowTrendingUpIcon className="h-5 w-5 mr-2" />
              Writing Trend
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={mockWritingTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="entries" 
                  stroke="#8B5CF6" 
                  strokeWidth={2}
                  dot={{ fill: '#8B5CF6' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Mood Distribution */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <h3 className="text-lg font-semibold text-ink-black mb-4">
              Mood Distribution
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={mockMoodData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {mockMoodData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Top Tags */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            <h3 className="text-lg font-semibold text-ink-black mb-4 flex items-center">
              <TagIcon className="h-5 w-5 mr-2" />
              Popular Tags
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={mockTagData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="tag" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Writing Streaks */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            <h3 className="text-lg font-semibold text-ink-black mb-4 flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2" />
              Writing Insights
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-green-800 font-medium">Current Streak</span>
                <span className="text-green-600 font-bold">7 days</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <span className="text-blue-800 font-medium">Longest Streak</span>
                <span className="text-blue-600 font-bold">23 days</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <span className="text-purple-800 font-medium">Avg. Words/Entry</span>
                <span className="text-purple-600 font-bold">
                  {Math.round(stats?.overview?.avgWordsPerEntry || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <span className="text-orange-800 font-medium">Most Active Day</span>
                <span className="text-orange-600 font-bold">Sunday</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
